import * as Yup from 'yup'
import { parseStringNumber, formatNumberString } from 'Utility/numberMethods'
import { validators } from 'Utility/validatorForm'
import { momentLongDateFormat } from 'Utility/dateMethods'
import moment from 'moment'

export const transactionsSchemas = (intl) => {
  const formatDate = momentLongDateFormat() 
  return ({
    minAmount: Yup.string()
      .test(
        'is-required',
        intl.formatMessage({
          id: 't.app.orders.research.errors.minRequired',
          defaultMessage: 'Min amount is required when Max amount is provided'
        }),
        (value, { parent: { maxAmount } }) => !(maxAmount && !value)
      )
      .test(
        'is-less-or-equal',
        intl.formatMessage({
          id: 't.app.orders.summary.order.error.amount.min.equal',
          defaultMessage: 'Must be less or equal than the maximum amount'
        }),
        (value, { parent: { maxAmount } }) => {
          if (!value || !maxAmount) return true
          const stringMinAmount = formatNumberString(value)
          const stringMaxAmount = formatNumberString(maxAmount)
          const parsedMinAmount = parseStringNumber(stringMinAmount)
          const parsedMaxAmount = parseStringNumber(stringMaxAmount)
          return (
            isNaN(parsedMinAmount) ||
      isNaN(parsedMaxAmount) ||
      parsedMinAmount === 0 ||
       parsedMinAmount <= parsedMaxAmount
          )
        }
      ),
    maxAmount: Yup.string()
      .test(
        'is-required',
        intl.formatMessage({
          id: 't.app.orders.research.errors.maxRequired',
          defaultMessage: 'Max amount is required when Min amount is provided'
        }),
        (value, { parent: { minAmount } }) => !(minAmount && !value)
      )
      .test(
        'is-greater-or-equal',
        intl.formatMessage({
          id: 't.app.orders.summary.order.error.amount.max.equal',
          defaultMessage: 'Must be greater or equal than the minimum amount'
        }),
        (value, { parent: { minAmount } }) => {
          if ((!value && value !== 0) || !minAmount) return true
          const parsedMinAmount = parseStringNumber(minAmount)
          const stringMaxAmount = formatNumberString(value)
          const parsedMaxAmount = parseStringNumber(stringMaxAmount)
          return isNaN(parsedMinAmount) || isNaN(parsedMaxAmount) || parsedMaxAmount >= parsedMinAmount
        }
      ),
    fromTime: Yup.string()
      .nullable()
      .test(
        'is-valid',
        intl.formatMessage({
          id: 't.app.pblManagement.research.validator.validDate',
          defaultMessage: 'Enter a valid date'
        }),
        (value) => !value || moment(value, formatDate).isValid()
      )
      .test(
        'is-less',
        intl.formatMessage({
          id: 't.app.orders.export.popup.timeError',
          defaultMessage: 'The time range is not valid'
        }),
        function (value) {
          const { toTime } = this.parent
          if (!toTime || !moment(value, formatDate).isValid() || !moment(toTime, formatDate).isValid()) {
            return true
          }
          return moment(value, formatDate).isSameOrBefore(moment(toTime, formatDate)) && moment(value, formatDate).isSameOrAfter(moment(toTime, formatDate).subtract(30, 'days'), 'day') && moment(value, formatDate).isSameOrAfter(moment().subtract(13, 'months'), 'day')
        }
      ).test(
        'is-not-future',
        intl.formatMessage({
          id: 't.app.pblManagement.research.validator.validDate',
          defaultMessage: 'Enter a valid date'
        }),
        (value) => {
          if (!moment(value, formatDate).isValid()) return true
          return moment(value, formatDate).isSameOrBefore(moment(), 'day')
        }
      ),
    toTime: Yup.string()
      .nullable()
      .test(
        'is-valid',
        intl.formatMessage({
          id: 't.app.pblManagement.research.validator.validDate',
          defaultMessage: 'Enter a valid date'
        }),
        (value) => !value || moment(value, formatDate).isValid()
      )
      .test(
        'is-greater',
        intl.formatMessage({
          id: 't.app.orders.export.popup.timeError',
          defaultMessage: 'The time range is not valid'
        }),
        function (value) {
          const { fromTime } = this.parent
          if (
            !fromTime ||
                !moment(value, formatDate).isValid() ||
                !moment(fromTime, formatDate).isValid()
          ) {
            return true
          }
          return moment(value, formatDate).isSameOrAfter(moment(fromTime, formatDate)) && moment(value, formatDate).isSameOrBefore(moment(fromTime, formatDate).add(30, 'days'), 'day') && moment(value, formatDate).isSameOrAfter(moment().subtract(13, 'months'), 'day')
        }
      ).test(
        'is-not-future',
        intl.formatMessage({
          id: 't.app.pblManagement.research.validator.validDate',
          defaultMessage: 'Enter a valid date'
        }),
        (value) => {
          if (!moment(value, formatDate).isValid()) return true
          return moment(value, formatDate).isSameOrBefore(moment(), 'day')
        }
      ),
    amount: Yup.string()
      .test('is-within-range', (value, { parent: { minAmount, maxAmount }, createError }) => {
        if (!minAmount || !maxAmount) return true

        const parsedAmount = parseStringNumber(formatNumberString(value))
        const parsedMinAmount = parseStringNumber(formatNumberString(minAmount))
        const parsedMaxAmount = parseStringNumber(formatNumberString(maxAmount))
        if (value && parsedAmount >= parsedMinAmount && parsedAmount <= parsedMaxAmount) return true

        return createError({
          message: intl.formatMessage(
            {
              id: 't.app.orders.operation.popup.input.error',
              defaultMessage: 'Enter a number between {num1} and {num2}'
            },
            { num1: minAmount, num2: maxAmount }
          )
        })
      })
      .test('is-greater-than', (value, { parent: { minAmount, maxAmount }, createError }) => {
        if (!minAmount || maxAmount) return true

        const parsedAmount = parseStringNumber(formatNumberString(value))
        const parsedMinAmount = parseStringNumber(formatNumberString(minAmount))
        if (value && parsedAmount > parsedMinAmount) return true

        return createError({
          message: intl.formatMessage(
            {
              id: 't.app.orders.operation.popup.input.greaterThanMin',
              defaultMessage: 'Enter a number greater than {minAmount}'
            },
            { minAmount }
          )
        })
      })
      .test('is-less-than', (value, { parent: { minAmount, maxAmount }, createError }) => {
        if (minAmount || !maxAmount) return true

        const parsedAmount = parseStringNumber(formatNumberString(value))
        const parsedMaxAmount = parseStringNumber(formatNumberString(maxAmount))
        if (value && parsedAmount < parsedMaxAmount) return true

        return createError({
          message: intl.formatMessage(
            {
              id: 't.app.orders.operation.popup.input.lessThanMax',
              defaultMessage: 'Enter a number less than {maxAmount}'
            },
            { maxAmount }
          )
        })
      }),
    advancedFilters: Yup.object({
      bonusAmountFrom: Yup.string().test(
        'is-greater-or-equal',
        intl.formatMessage({
          id: 't.app.orders.summary.order.error.amount.min.equal',
          defaultMessage: 'Must be less or equal than the maximum amount'
        }),
        (value, { parent: { bonusAmountTo } }) => {
          if (!value || !bonusAmountTo) return true
          const parsedbonusAmountFrom = parseStringNumber(formatNumberString(value))
          const parsedbonusAmountTo = parseStringNumber(formatNumberString(bonusAmountTo))
          return (
            isNaN(parsedbonusAmountFrom) ||
          isNaN(parsedbonusAmountTo) ||
          parsedbonusAmountFrom === 0 ||
          parsedbonusAmountFrom <= parsedbonusAmountTo
          )
        }
      ),
      bonusAmountTo: Yup.string().test(
        'is-greater-or-equal',
        intl.formatMessage({
          id: 't.app.orders.summary.order.error.amount.max.equal',
          defaultMessage: 'Must be greater or equal than the minimum amount'
        }),
        (value, { parent: { bonusAmountFrom } }) => {
          if (!value || !bonusAmountFrom) return true
          const parsedbonusAmountTo = parseStringNumber(formatNumberString(value))
          const parsedbonusAmountFrom = parseStringNumber(formatNumberString(bonusAmountFrom))
          return (
            isNaN(parsedbonusAmountTo) ||
          isNaN(parsedbonusAmountFrom) ||
          parsedbonusAmountTo === 0 ||
          parsedbonusAmountTo >= parsedbonusAmountFrom
          )
        }
      )
    })
  })}

export const termsAndConditionsSchema = (intl) => ({
  type: Yup.string().required(
    intl.formatMessage({
      id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
      defaultMessage: 'Required field'
    })
  ),
  name: Yup.string().required(
    intl.formatMessage({
      id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
      defaultMessage: 'Required field'
    })
  ),
  reservationType: Yup.string().required(
    intl.formatMessage({
      id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
      defaultMessage: 'Required field'
    })
  ),
  termsAndConditions: Yup.array()
    .of(
      Yup.object().shape({
        language: Yup.string().required(
          intl.formatMessage({
            id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
            defaultMessage: 'Required field'
          })
        ),
        text: Yup.string().required(
          intl.formatMessage({
            id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
            defaultMessage: 'Required field'
          })
        )
      })
    ) // Test for the presence of an English entry with non-empty text.
    .test(
      'has-english',
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.language.error',
        defaultMessage: 'You must include terms and conditions in English.'
      }),
      function (terms = []) {
        const { path, createError } = this
        // Look for an entry where language is 'ENG' and text is non-empty.
        const englishIndex = terms.findIndex(
          (term) => term.language === 'ENG' && term.text && term.text.trim().length > 0
        )
        if (englishIndex === -1) {
          // If an entry exists with language 'ENG' but empty text, attach error to its text field.
          const engEntryIndex = terms.findIndex((term) => term.language === 'ENG')
          if (engEntryIndex !== -1) {
            return createError({
              path: `${path}[${engEntryIndex}].text`,
              message: 'Terms and conditions in English must be filled.'
            })
          }
          // Otherwise, attach the error to the first language field.
          return createError({
            path: `${path}[0].language`,
            message: intl.formatMessage({
              id: 't.app.terminalsManagement.disputeless.form.language.error',
              defaultMessage: 'You must include terms and conditions in English.'
            })
          })
        }
        return true
      }
    )
    // Test for unique languages.
    .test(
      'unique-language',
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.language.check',
        defaultMessage: 'Each language must be entered only once'
      }),
      function (terms = []) {
        const { path, createError } = this
        const seen = new Set()
        for (let i = 0; i < terms.length; i++) {
          const lang = terms[i].language
          if (seen.has(lang)) {
            return createError({
              path: `${path}[${i}].language`,
              message: intl.formatMessage({
                id: 't.app.terminalsManagement.disputeless.form.language.check',
                defaultMessage: 'Each language must be entered only once'
              })
            })
          }
          seen.add(lang)
        }
        return true
      }
    )
})

export const structureSchema = (intl) => ({
  name: Yup.string().required(
    intl.formatMessage({
      id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
      defaultMessage: 'Required field'
    })
  ),
  email: Yup.string()
    .email(
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.errorEmail',
        defaultMessage: 'Invalid email'
      })
    )
    .test(
      'custom-validation',
      intl.formatMessage({
        id: 't.app.paybylink.request.validator.validEmail',
        defaultMessage: 'Enter a valid email'
      }),
      function (value) {
        return validators.isEmail(value)
      }
    )
    .required(
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
        defaultMessage: 'Required field'
      })
    ),
  phoneCountryCode: Yup.string().required(
    intl.formatMessage({
      id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
      defaultMessage: 'Required field'
    })
  ),
  phone: Yup.string()
    .matches(
      /^\d{6,15}$/,
      intl.formatMessage({
        id: 't.app.paybylink.request.other.info.validator.phoneNumber',
        defaultMessage: 'it is a numeric field and can be between 6 and 15 digits.'
      })
    )
    .required(
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.fieldRequired',
        defaultMessage: 'Required field'
      })
    ),
  checkIn: Yup.object().shape({
    from: Yup.mixed().test(
      'checkIn-from-valid-time',
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.errTimeRange',
        defaultMessage: 'Invalid time range'
      }),
      function (value) {
        const { to } = this.parent
        if (!value || !to) return true
        return moment(value).format('HH:mm') < moment(to).format('HH:mm')
      }
    ),
    to: Yup.mixed().test('checkIn-to-valid-time', ' ', function () {
      const { from, to } = this.parent
      if (!from || !to) return true
      return moment(from).format('HH:mm') < moment(to).format('HH:mm')
    })
  }),
  checkOut: Yup.object().shape({
    from: Yup.mixed().test(
      'checkOut-from-valid-time',
      intl.formatMessage({
        id: 't.app.terminalsManagement.disputeless.form.errTimeRange',
        defaultMessage: 'Invalid time range'
      }),
      function (value) {
        const { to } = this.parent
        if (!value || !to) return true
        return moment(value).format('HH:mm') < moment(to).format('HH:mm')
      }
    ),
    to: Yup.mixed().test('checkOut-to-valid-time', ' ', function () {
      const { from, to } = this.parent
      if (!from || !to) return true
      return moment(from).format('HH:mm') < moment(to).format('HH:mm')
    })
  })
})

/**
 * @name getYupSchema
 * @description - Generates a Yup validation schema based on the provided validation schema and keys.
 * @param {Function} validationSchema - A function that returns a Yup validation schema object.
 * @param {Object} intl - An internationalization object used by the validation schema function.
 * @param {Array<string>} [keys=[]] - An optional array of keys to include in the final validation schema.
 * @returns {Object} - A Yup validation schema object containing only the specified keys.
 */
export const getYupSchema = (validationSchema, intl, keys, obj) => {
  const yupSchemas = validationSchema(intl, obj)
  if (!keys?.length) {
    return Yup.object().shape(yupSchemas)
  }

  const schema = keys.reduce((result, key) => {
    if (key in yupSchemas) {
      result[key] = yupSchemas[key]
    }
    return result
  }, {})

  return Yup.object().shape(schema)
}
